import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, Upload, Sparkles, Check, RefreshCw, Trash2, ArrowRight, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { salesApi, apiError, compressImage } from '../salesApi';
import { useT } from '../salesI18n';
import { useSalesAuth } from '../SalesAuthContext';

const blobUrl = async (which) => {
  const r = await salesApi.get(`/auth/photo?which=${which}`, { responseType: 'blob' });
  return URL.createObjectURL(r.data);
};

// "Minha foto": selfie sorrindo → IA gera o retrato profissional → vendedor aprova/refaz. `required` = sem foto ainda (não fecha).
export const RepPhotoDialog = ({ open, onOpenChange, required = false }) => {
  const { t } = useT();
  const p = t.photo;
  const { user, refresh } = useSalesAuth();
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [stage, setStage] = useState('idle'); // idle | generating | review
  const [busy, setBusy] = useState(false);
  const [imgs, setImgs] = useState({ pro: null, original: null, draft: null });

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const urls = [];
    (async () => {
      const next = { pro: null, original: null, draft: null };
      if (user?.has_photo) next.pro = await blobUrl('pro').catch(() => null);
      if (user?.has_photo_draft) {
        [next.original, next.draft] = await Promise.all([blobUrl('original').catch(() => null), blobUrl('draft').catch(() => null)]);
      }
      if (!alive) return;
      urls.push(...Object.values(next).filter(Boolean));
      setImgs(next);
      setStage(next.draft ? 'review' : 'idle');
    })();
    return () => { alive = false; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [open, user?.has_photo, user?.has_photo_draft, user?.photo_updated_at]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImgs((s) => ({ ...s, original: URL.createObjectURL(file), draft: null }));
    setStage('generating');
    try {
      const fd = new FormData();
      fd.append('file', await compressImage(file, 1600, 0.9));
      await salesApi.post('/auth/photo', fd, { timeout: 200000 });
      setImgs((s) => ({ ...s, draft: null }));
      const draft = await blobUrl('draft');
      setImgs((s) => ({ ...s, draft }));
      setStage('review');
    } catch (err) {
      toast.error(apiError(err, p.failed));
      setStage('idle');
    }
  };

  const regenerate = async () => {
    setStage('generating');
    try {
      await salesApi.post('/auth/photo/regenerate', null, { timeout: 200000 });
      const draft = await blobUrl('draft');
      setImgs((s) => ({ ...s, draft }));
      setStage('review');
    } catch (err) { toast.error(apiError(err, p.failed)); setStage('review'); }
  };

  const approve = async () => {
    setBusy(true);
    try { await salesApi.post('/auth/photo/approve'); await refresh(); toast.success(p.saved); onOpenChange(false); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await salesApi.delete('/auth/photo'); await refresh(); toast.success(p.removed); setStage('idle'); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const pickers = (
    <div className="grid grid-cols-2 gap-3" data-testid="rep-photo-pickers">
      <button type="button" onClick={() => camRef.current?.click()} data-testid="rep-photo-take-selfie" className="flex flex-col items-center justify-center gap-2 h-28 rounded-2xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm shadow-[0_8px_20px_-8px_rgba(13,148,136,0.7)] transition-all active:scale-95">
        <Camera className="h-7 w-7" /> {p.takeSelfie}
      </button>
      <button type="button" onClick={() => fileRef.current?.click()} data-testid="rep-photo-upload" className="flex flex-col items-center justify-center gap-2 h-28 rounded-2xl border-2 border-dashed border-teal-300 bg-teal-50/50 hover:bg-teal-50 text-teal-800 font-semibold text-sm transition-colors active:scale-95">
        <Upload className="h-7 w-7" /> {p.upload}
      </button>
      <input ref={camRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} data-testid="rep-photo-camera-input" />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="rep-photo-file-input" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!required || v) onOpenChange(v); }}>
      <DialogContent className={`max-w-lg ${required ? '[&>button]:hidden' : ''}`} data-testid="rep-photo-dialog" onInteractOutside={(e) => required && e.preventDefault()} onEscapeKeyDown={(e) => required && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-teal-600" /> {p.title}
            {required && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5" data-testid="rep-photo-required-badge">{p.requiredBadge}</span>}
          </DialogTitle>
          {required && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="rep-photo-required-hint">{p.requiredHint}</p>}
        </DialogHeader>

        {stage === 'idle' && (
          <div className="space-y-4">
            {imgs.pro && (
              <div className="flex items-center gap-4 rounded-2xl border border-teal-200 bg-teal-50/60 p-3" data-testid="rep-photo-current">
                <img src={imgs.pro} alt="" className="h-20 w-20 rounded-full object-cover border-[3px] border-amber-400 shadow" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{user?.full_name}</p>
                  <p className="text-xs text-slate-600">{p.current}</p>
                </div>
                <button type="button" onClick={remove} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:underline shrink-0" data-testid="rep-photo-remove"><Trash2 className="h-3.5 w-3.5" /> {p.remove}</button>
              </div>
            )}
            <p className="text-sm text-slate-700 leading-relaxed" data-testid="rep-photo-hint">{p.hint}</p>
            <ul className="grid gap-1.5 text-xs text-slate-600">
              {p.tips.map((tip) => <li key={tip} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-teal-600 shrink-0" /> {tip}</li>)}
            </ul>
            {pickers}
          </div>
        )}

        {stage === 'generating' && (
          <div className="flex flex-col items-center gap-4 py-4" data-testid="rep-photo-generating">
            <div className="relative">
              {imgs.original && <img src={imgs.original} alt="" className="h-40 w-40 rounded-full object-cover border-4 border-teal-200 opacity-70" />}
              <span className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-10 w-10 text-teal-600 animate-spin" /></span>
            </div>
            <p className="text-sm font-semibold text-slate-900">{p.generating}</p>
            <p className="text-xs text-slate-500">{p.generatingHint}</p>
          </div>
        )}

        {stage === 'review' && (
          <div className="space-y-4" data-testid="rep-photo-review">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <figure className="text-center">
                {imgs.original ? <img src={imgs.original} alt="" className="mx-auto h-32 w-32 sm:h-40 sm:w-40 rounded-2xl object-cover border" data-testid="rep-photo-before" /> : <div className="mx-auto h-32 w-32 sm:h-40 sm:w-40 rounded-2xl bg-slate-100" />}
                <figcaption className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{p.before}</figcaption>
              </figure>
              <ArrowRight className="h-6 w-6 text-teal-600" />
              <figure className="text-center">
                {imgs.draft ? <img src={imgs.draft} alt="" className="mx-auto h-32 w-32 sm:h-40 sm:w-40 rounded-2xl object-cover border-[3px] border-amber-400 shadow-lg" data-testid="rep-photo-after" /> : <div className="mx-auto h-32 w-32 sm:h-40 sm:w-40 rounded-2xl bg-slate-100 animate-pulse" />}
                <figcaption className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-teal-700">{p.after}</figcaption>
              </figure>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="h-11" onClick={regenerate} disabled={busy} data-testid="rep-photo-retry"><RefreshCw className="h-4 w-4 mr-1.5" /> {p.retry}</Button>
              <Button type="button" variant="outline" className="h-11" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="rep-photo-another"><Upload className="h-4 w-4 mr-1.5" /> {p.another}</Button>
            </div>
            <Button type="button" className="w-full h-12 font-semibold bg-teal-600 hover:bg-teal-700" onClick={approve} disabled={busy || !imgs.draft} data-testid="rep-photo-approve"><Check className="h-4 w-4 mr-1.5" /> {p.approve}</Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="rep-photo-file-input-review" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
